import type { FC } from "react";
import AttachmentsField from "@/components/controls/AttachmentsField";
import type { SavedAttachmentInfo } from "@/entities/attachments/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  allowsMultipleFiles,
  getFileFieldAccept
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import { HALF_WRAPPER_CLASSNAME } from "../shared";
import type { ControlProps } from "../types";

export const FileUploadControl: FC<ControlProps> = ({
  value,
  onChange,
  name,
  config
}) => {
  // Every path that renders a file question resolves its control through the
  // registry, so the rollout gate lives here rather than in each caller. It
  // stays inside this control rather than in the registry lookup so the other
  // field types keep rendering without the feature-flag context.
  const { enabled: isFileFieldEnabled, loading: isFileFieldFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_9188_FILE_FIELD_MGMT);
  const attachments = Array.isArray(value)
    ? (value as SavedAttachmentInfo[])
    : [];

  // Nothing rather than the text fallback: the fallback would expose the
  // stored attachment JSON in an input the answer could be overwritten with.
  if (isFileFieldFlagLoading || !isFileFieldEnabled) {
    return null;
  }

  return (
    <div className={HALF_WRAPPER_CLASSNAME}>
      <AttachmentsField
        // Record fields carry the same composite as the form: an empty field
        // opens on the Large drag-and-drop area, and a populated one caps the
        // list and reveals the rest on demand. Left to the "preview" default
        // the empty state collapsed to a compact button and the populated list
        // became an unlabelled scroll box with no reveal control.
        variant="edit"
        value={attachments}
        onChange={(newValue) => onChange(newValue || [])}
        name={name}
        accept={getFileFieldAccept(config?.supportedFiles)}
        isSingleFile={!allowsMultipleFiles(config)}
      />
    </div>
  );
};
